import { FC, MouseEvent } from 'react';
import useTheme from '../../utils/useTheme';
import { Icon } from '../Icon/Icon';
import { BadgeContent, BadgeElement, BadgeIcon, getIconColor } from './Badge.styles';
import { SvgIcon } from '../SvgIcons/svgIcons';

export type BadgeVariation = 'normal' | 'outline' | 'basic' | 'clean';

export interface IProps extends IntrinsicProps {
  /** 'normal' (default) | 'primary' | 'outline' | 'basic' */
  variation?: BadgeVariation;
  /** The color of the icon */
  iconColor?: string;
  /** The background color of the badge - the foreground is always white */
  color?: string;
  /** The border color of the badge - overrides default border color for the variation */
  borderColor?: string;
  /** Optional name of icon which appears to the right of the badge's label */
  icon?: string;
  useSvgIcon?: boolean;
  displayIconLeft?: boolean;
  /** Optional size of the badge. Defaults to 'normal'. */
  size?: 'normal' | 'small';
  /** Optional prop to make the corners rounded - boolean for default, or integer for specific border radius */
  rounded?: boolean | number;
  /** Called when button is clicked with the event passed in */
  onClick?: (e: MouseEvent) => void;
  /** Data TestID for testing */
  dataTestID?: string;
}

export const Badge: FC<IProps> = ({ useSvgIcon, displayIconLeft, ...props }) => {
  const theme = useTheme();

  const IconEl = () => (
    <BadgeIcon>
      {useSvgIcon ? (
        <SvgIcon name={props.icon as any} color={getIconColor(props, theme)} size="16" />
      ) : (
        <Icon icon={props.icon} color={getIconColor(props, theme)} size="extra-small" />
      )}
    </BadgeIcon>
  );

  const LegacyLayout = () => (
    <>
      {props.icon && !displayIconLeft && <BadgeContent>{props.children}</BadgeContent>}
      {!displayIconLeft && props.icon && <IconEl />}
    </>
  );

  return (
    <BadgeElement
      {...props}
      variation={props.variation}
      onClick={props.onClick}
      data-testid={props.dataTestID ? props.dataTestID : 'Badge'}
    >
      {!props.icon && props.children}

      {/**
       * To support new designs, this piece of the code been separated, and the legacy code can be
       * found in <LegacyLayout />. The legacy layout will be displayed if displayIconLeft is falsey and
       * icon has been provided. The new layout will be shown based on an opt in approach, and will only
       * show if displayIconLeft is truthy and icon has been provided.
       */
      displayIconLeft && props.icon && (
        <span style={{ display: 'flex', flexDirection: 'row', gap: theme.designToken.spacing1 }}>
          {displayIconLeft && props.icon && <IconEl />}
          {props.children}
        </span>
      )}

      <LegacyLayout />
    </BadgeElement>
  );
};

Badge.defaultProps = {
  variation: 'normal',
  size: 'normal',
};
